import { c as _c } from "react/compiler-runtime";
import { usePageView } from '@/analytics/pageTypes';
import { Layout } from '@/components/layout';
import Hero from '@/sections/Hero';
import { SectionDelimiter } from '@/components/SectionDelimiter';
import { SectionHeadline } from '@/components/SectionHeadline';
import { type IntlProps, getIntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import { Trans, useTranslation } from 'react-i18next';
import { LINKS } from '@/constants/links';
import React from 'react';
import { Csob } from '@/sections/testimonials/Csob';
import { MoreInfo } from '@/sections/testimonials/MoreInfo';
import { TestimonialsGoogle } from '@/sections/testimonials/TestimonialsGoogle';
import { TestimonialsFacebook } from '@/sections/testimonials/TestimonialsFacebook';
import { TestimonialsApp } from '@/sections/testimonials/TestimonialsApp';
import HeroSimple from '@/sections/HeroSimple';
import { Link } from '@/components/links';
import { Button } from '@/components/buttons';
import { CONTACT_EMAIL } from '@/constants/contacts';
import { getTestimonialsBySlug, Testimonial } from '@/api/sanity/testimonials';
import { getPageBannerBySlug, PageBanner } from '@/api/sanity/pageBanners';
import { SeoHeader } from '@/components/SeoHeader';
import { isAllowedRoute } from '@/lang/pagesConfig';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
interface Props extends IntlProps {
  pageBanner: PageBanner | null;
  menuPromoBanners?: PageMenuPromoBanner[];
  testimonials: {
    app: Testimonial[];
    facebook: Testimonial[];
    google: Testimonial[];
  };
}
export default function Page(props) {
  const $ = _c(83);
  usePageView("testimonials");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["testimonials", "core"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const {
    pageBanner,
    menuPromoBanners,
    testimonials
  } = props;
  let t1;
  if ($[1] !== t) {
    t1 = t("seo.title");
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t) {
    t2 = t("seo.description");
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <SeoHeader title={t1} description={t2} />;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const t4 = pageBanner?.videoId;
  let t5;
  if ($[8] !== pageBanner?.title || $[9] !== t) {
    t5 = pageBanner?.title ?? t("banner.title");
    $[8] = pageBanner?.title;
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== pageBanner?.text || $[12] !== t) {
    t6 = pageBanner?.text ?? <Trans t={t} i18nKey="banner.text"><Link to={`mailto:${CONTACT_EMAIL}`} testId="testimonialsEmail" analyticsParam="testimonialsEmail" /></Trans>;
    $[11] = pageBanner?.text;
    $[12] = t;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== pageBanner || $[15] !== t) {
    t7 = pageBanner ? pageBanner.buttons : [{
      ariaLabel: t("banner.cta"),
      label: t("banner.cta"),
      to: LINKS().referral,
      testId: "testimonialsHeroPrimaryCTA",
      analyticsParam: "testimonialsHeroPrimaryCTA",
      variant: "primary"
    }];
    $[14] = pageBanner;
    $[15] = t;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const t8 = pageBanner?.image?.url ?? "/images/sections/testimonials/hero/cs/hero.png";
  let t9;
  if ($[17] !== pageBanner?.image?.alt || $[18] !== t) {
    t9 = pageBanner?.image?.alt ?? t("banner.title");
    $[17] = pageBanner?.image?.alt;
    $[18] = t;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t8 || $[21] !== t9) {
    t10 = {
      url: t8,
      alt: t9
    };
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const t11 = pageBanner?.variant || "mockupMobile";
  let t12;
  if ($[23] !== t10 || $[24] !== t11 || $[25] !== t4 || $[26] !== t5 || $[27] !== t6 || $[28] !== t7) {
    t12 = <Hero videoId={t4} title={t5} text={t6} buttons={t7} image={t10} variant={t11} />;
    $[23] = t10;
    $[24] = t11;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="bg-blue-50"><SectionDelimiter color="bg-blue-100" background="bg-transparent" /></div>;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  let t15;
  let t16;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="bgCircle absolute top-1/3 left-0 -translate-x-1/4 lg:top-0 lg:translate-y-1/2" />;
    t15 = <div className="bgCircle absolute right-0 bottom-20 translate-x-1/4 -rotate-90 lg:bottom-1/4 lg:translate-y-1/2" />;
    t16 = <div className="bgCircle absolute top-0 right-0 translate-x-1/4 lg:hidden" />;
    $[31] = t14;
    $[32] = t15;
    $[33] = t16;
  } else {
    t14 = $[31];
    t15 = $[32];
    t16 = $[33];
  }
  let t17;
  if ($[34] !== testimonials.facebook) {
    t17 = <TestimonialsFacebook testimonials={testimonials.facebook} />;
    $[34] = testimonials.facebook;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== testimonials.app) {
    t18 = <TestimonialsApp testimonials={testimonials.app} />;
    $[36] = testimonials.app;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== testimonials.google) {
    t19 = <TestimonialsGoogle testimonials={testimonials.google} />;
    $[38] = testimonials.google;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== t17 || $[41] !== t18 || $[42] !== t19) {
    t20 = <div className="relative z-30 overflow-x-hidden bg-blue-100 pt-10 pb-20 lg:pt-20 lg:pb-40">{t14}{t15}{t16}<div className="relative z-10 flex flex-col gap-20 lg:gap-40">{t17}{t18}{t19}</div></div>;
    $[40] = t17;
    $[41] = t18;
    $[42] = t19;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <SectionDelimiter color="bg-blue-50" background="bg-blue-100" />;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  let t23;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="relative bg-blue-50 px-4 pt-10 pb-20 lg:pt-20 lg:pb-40"><Csob /></div>;
    t23 = <SectionDelimiter color="bg-blue-100" background="bg-blue-50" />;
    $[45] = t22;
    $[46] = t23;
  } else {
    t22 = $[45];
    t23 = $[46];
  }
  let t24;
  if ($[47] !== t) {
    t24 = t("learnMore.title");
    $[47] = t;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] !== t24) {
    t25 = <SectionHeadline className="mx-auto mb-4 max-w-142 lg:mb-6" anchorKey="learnMore.title">{t24}</SectionHeadline>;
    $[49] = t24;
    $[50] = t25;
  } else {
    t25 = $[50];
  }
  let t26;
  if ($[51] !== t) {
    t26 = t("learnMore.text.1");
    $[51] = t;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  let t27;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <br />;
    $[53] = t27;
  } else {
    t27 = $[53];
  }
  let t28;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <Link to={LINKS().wroteAboutUs} testId="testimonialsWroteAboutUs" analyticsParam="testimonialsWroteAboutUs" />;
    $[54] = t28;
  } else {
    t28 = $[54];
  }
  let t29;
  if ($[55] !== t) {
    t29 = <Trans t={t} i18nKey="learnMore.text.2">{t28}</Trans>;
    $[55] = t;
    $[56] = t29;
  } else {
    t29 = $[56];
  }
  let t30;
  if ($[57] !== t26 || $[58] !== t29) {
    t30 = <p className="text-center text-sm lg:text-base">{t26}{t27}{t29}</p>;
    $[57] = t26;
    $[58] = t29;
    $[59] = t30;
  } else {
    t30 = $[59];
  }
  let t31;
  if ($[60] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = LINKS();
    $[60] = t31;
  } else {
    t31 = $[60];
  }
  const t32 = t31.aboutCompany;
  let t33;
  if ($[61] !== t) {
    t33 = t("learnMore.cta");
    $[61] = t;
    $[62] = t33;
  } else {
    t33 = $[62];
  }
  let t34;
  if ($[63] !== t33) {
    t34 = <Button to={t32} testId="testimonialsAboutCompany" analyticsParam="testimonialsAboutCompany" size="lg" variant="primary" align="center" className="mt-10 md:mt-12">{t33}</Button>;
    $[63] = t33;
    $[64] = t34;
  } else {
    t34 = $[64];
  }
  let t35;
  if ($[65] !== t30 || $[66] !== t34) {
    t35 = <div className="px-4">{t30}{t34}</div>;
    $[65] = t30;
    $[66] = t34;
    $[67] = t35;
  } else {
    t35 = $[67];
  }
  let t36;
  if ($[68] !== t25 || $[69] !== t35) {
    t36 = <HeroSimple circleClassName="hidden" contentClassName="py-10 lg:py-20">{t25}{t35}</HeroSimple>;
    $[68] = t25;
    $[69] = t35;
    $[70] = t36;
  } else {
    t36 = $[70];
  }
  let t37;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <MoreInfo />;
    $[71] = t37;
  } else {
    t37 = $[71];
  }
  let t38;
  if ($[72] !== t36) {
    t38 = <div className="relative z-10">{t36}{t37}</div>;
    $[72] = t36;
    $[73] = t38;
  } else {
    t38 = $[73];
  }
  let t39;
  if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <div className="bgCircle absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 rotate-180" />;
    $[74] = t39;
  } else {
    t39 = $[74];
  }
  let t40;
  if ($[75] !== t38) {
    t40 = <div className="relative overflow-hidden bg-blue-100">{t38}{t39}</div>;
    $[75] = t38;
    $[76] = t40;
  } else {
    t40 = $[76];
  }
  let t41;
  if ($[77] !== menuPromoBanners || $[78] !== t12 || $[79] !== t20 || $[80] !== t3 || $[81] !== t40) {
    t41 = <Layout menuPromoBanners={menuPromoBanners}>{t3}{t12}{t13}{t20}{t21}{t22}{t23}{t40}</Layout>;
    $[77] = menuPromoBanners;
    $[78] = t12;
    $[79] = t20;
    $[80] = t3;
    $[81] = t40;
    $[82] = t41;
  } else {
    t41 = $[82];
  }
  return t41;
}
export const getStaticProps = (async ctx => {
  const isAllowed = isAllowedRoute('recenze-klientu');
  if (!isAllowed) {
    return {
      notFound: true
    };
  }
  return {
    props: {
      pageBanner: await getPageBannerBySlug('testimonials'),
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      testimonials: {
        app: await getTestimonialsBySlug('testimonials-app'),
        facebook: await getTestimonialsBySlug('testimonials-facebook'),
        google: await getTestimonialsBySlug('testimonials-google')
      },
      ...(await getIntlProps(ctx, ['core', 'testimonials', 'insurance']))
    }
  };
}) satisfies GetStaticProps<Props>;